"use server";

import { getWorkOS } from "@workos-inc/authkit-nextjs";
import { cookies } from "next/headers";
import {
  PENDING_AUTHENTICATION_COOKIE,
  unsealPendingAuthentication,
} from "@/lib/auth/pending-authentication";
import { api } from "@convex/_generated/api";
import { fetchQuery } from "@/lib/convex/tracked";
import { createPasswordAccount, setAccountPassword } from "@/lib/auth/sign-in/create-account";
import { readCode, readEmail, readListingContinuation, readText } from "@/lib/auth/sign-in/input";
import { MIN_PASSWORD_LENGTH, SHORT_PASSWORD_MESSAGE } from "@/lib/auth/sign-in/password-policy";
import { submitListingRequest } from "@/lib/auth/sign-in/listing-request";
import { allowSignInAttempt } from "@/lib/auth/sign-in/rate-limit";
import { readSignInRequestContext } from "@/lib/auth/sign-in/request-context";
import {
  INVALID_CODE_MESSAGE,
  signInError,
  toSignInResult,
  type SignInResult,
} from "@/lib/auth/sign-in/result";
import { signIn, workosStatus } from "@/lib/auth/sign-in/sign-in";
import { captureServerError } from "@/lib/posthog-server";

const RATE_LIMITED_MESSAGE = "Too many attempts. Wait a minute, then try again.";
const QUOTE_LINK_INACTIVE_MESSAGE =
  "This quote link is no longer active. Ask your Repackify rep for a new one.";

export async function signInWithPassword(input: {
  email: string;
  password: string;
  quoteClaimToken?: string;
}): Promise<SignInResult> {
  const email = readEmail(input);
  const password = readText(input, "password");
  if (!email || !password) return signInError("Enter your email and password.", "invalid_input");
  return toSignInResult(
    await signIn({
      grant: { kind: "password", email, password },
      area: "auth_password",
      signupVariant: "password",
      reportAuthEventOnNextPage: false,
      quoteClaimToken: readText(input, "quoteClaimToken"),
    }),
  );
}

export async function signUpWithPassword(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  quoteClaimToken?: string;
}): Promise<SignInResult> {
  const email = readEmail(input);
  const firstName = readText(input, "firstName")?.trim();
  const lastName = readText(input, "lastName")?.trim();
  const password = readText(input, "password");
  if (!email || !firstName || !lastName || !password) {
    return signInError("Enter your name, email and a password.", "invalid_input");
  }
  const { ipAddress } = await readSignInRequestContext();
  if (!(await allowSignInAttempt("sign_up", { email, ipAddress }))) {
    return signInError(RATE_LIMITED_MESSAGE, "rate_limited");
  }
  const refusal = await createPasswordAccount({ firstName, lastName, email, password });
  if (refusal) return signInError(refusal.message, refusal.reason);
  return signInWithPassword({ email, password, quoteClaimToken: readText(input, "quoteClaimToken") });
}

export async function signInWithEmailCode(input: {
  email: string;
  code: string;
  quoteClaimToken?: string;
  listingRequest?: unknown;
}): Promise<SignInResult> {
  const email = readEmail(input);
  const code = readCode(input);
  const continuation = readListingContinuation(input);
  if (!email || continuation === null) return signInError("Check your details and try again.", "invalid_input");
  if (!code) return signInError(INVALID_CODE_MESSAGE, "invalid_code");
  const outcome = await signIn({
    grant: { kind: "email_code", email, code },
    area: "auth_email_code",
    signupVariant: "magic_code",
    reportAuthEventOnNextPage: false,
    quoteClaimToken: readText(input, "quoteClaimToken"),
  });
  const result = toSignInResult(outcome);
  if (outcome.status !== "signed_in" || result.status !== "signed_in" || !continuation) {
    return result;
  }
  return {
    ...result,
    listingRequest: await submitListingRequest(outcome.accessToken, continuation, outcome.provisioning),
  };
}

export async function createQuoteAccount(input: {
  token: string;
  code: string;
  password: string;
}): Promise<SignInResult> {
  const token = readText(input, "token");
  const password = readText(input, "password");
  const code = readCode(input);
  if (!token || !password) return signInError("Create a password to continue.", "invalid_input");
  if (password.length < MIN_PASSWORD_LENGTH) return signInError(SHORT_PASSWORD_MESSAGE, "weak_password");
  if (!code) return signInError(INVALID_CODE_MESSAGE, "invalid_code");
  const card = await fetchQuery(api.quotes.queries.publicView.getClaimCardByToken, { token });
  if (!card?.isOpen) return signInError(QUOTE_LINK_INACTIVE_MESSAGE, "expired");
  return toSignInResult(
    await signIn({
      grant: { kind: "email_code", email: card.recipientEmail, code },
      area: "auth_quote_account",
      signupVariant: "magic_code",
      reportAuthEventOnNextPage: false,
      quoteClaimToken: card.claimed ? undefined : token,
      afterAuthenticate: (user) => setAccountPassword(user.id, password),
    }),
  );
}

export async function confirmPendingSignIn(input: { code: string }): Promise<SignInResult> {
  const code = readCode(input);
  if (!code) return signInError(INVALID_CODE_MESSAGE, "invalid_code");
  const pending = await unsealPendingAuthentication(
    (await cookies()).get(PENDING_AUTHENTICATION_COOKIE)?.value,
  );
  if (!pending) return signInError("This sign-in expired. Start again.", "expired");
  return toSignInResult(
    await signIn({
      grant: { kind: "pending_code", pending, code },
      area: "auth_pending_code",
      signupVariant: pending.signupVariant,
      reportAuthEventOnNextPage: false,
    }),
  );
}

export async function sendPasswordReset(input: {
  email: string;
}): Promise<{ status: "sent" } | { status: "error"; message: string }> {
  const email = readEmail(input);
  if (!email) return { status: "error", message: "Enter the email you signed up with." };
  const { ipAddress } = await readSignInRequestContext();
  if (!(await allowSignInAttempt("password_reset", { email, ipAddress }))) {
    return { status: "error", message: RATE_LIMITED_MESSAGE };
  }
  try {
    await getWorkOS().userManagement.createPasswordReset({ email });
  } catch (error) {
    const status = workosStatus(error);
    if (status === 429) return { status: "error", message: RATE_LIMITED_MESSAGE };
    if (status === null || status >= 500) {
      await captureServerError(error, { tags: { area: "auth_password_reset" } });
      return { status: "error", message: "We couldn't send a reset link. Please try again." };
    }
  }
  return { status: "sent" };
}
